import type {
  DashboardForecastEvent,
  DashboardForecastRow,
  DashboardPeriod
} from './types';

interface LocalDateParts {
  year: number;
  month: number;
  day: number;
}

interface BuildForecastRowsInput {
  startDate: string;
  endDate: string;
  openingBalance: number;
  eventsByDate: Record<string, DashboardForecastEvent[]>;
}

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function resolvePeriodEndDate(
  startDate: string,
  period: DashboardPeriod
): string {
  const start = parseLocalDate(startDate);

  if (period === '30d') {
    return formatLocalDate(addDays(start, 29));
  }

  if (period === '3m') {
    return formatLocalDate(addMonthsAsInclusivePeriodEnd(start, 3));
  }

  if (period === '12m') {
    return formatLocalDate(addMonthsAsInclusivePeriodEnd(start, 12));
  }

  throw new Error(`Unsupported dashboard period: ${String(period)}`);
}

export function isBelowSafetyThreshold(
  closingBalance: number,
  safetyThreshold: number
): boolean {
  return safetyThreshold > 0 && closingBalance < safetyThreshold;
}

export function buildForecastRows(
  input: BuildForecastRowsInput
): DashboardForecastRow[] {
  const start = parseLocalDate(input.startDate);
  const end = parseLocalDate(input.endDate);

  if (!Number.isSafeInteger(input.openingBalance)) {
    throw new Error('Opening balance must be an integer');
  }

  const startDay = toEpochDay(start);
  const endDay = toEpochDay(end);

  if (endDay < startDay) {
    throw new Error('End date must be on or after start date');
  }

  const rows: DashboardForecastRow[] = [];
  let openingBalance = input.openingBalance;

  for (let day = startDay; day <= endDay; day += 1) {
    const date = formatLocalDate(fromEpochDay(day));
    const events = input.eventsByDate[date] ?? [];
    const inflow = sumEventAmounts(events, (amount) => amount > 0);
    const outflow = Math.abs(sumEventAmounts(events, (amount) => amount < 0));
    const closingBalance = openingBalance + inflow - outflow;

    rows.push({
      date,
      openingBalance,
      inflow,
      outflow,
      closingBalance,
      events
    });

    openingBalance = closingBalance;
  }

  return rows;
}

function parseLocalDate(value: string): LocalDateParts {
  const match = LOCAL_DATE_PATTERN.exec(value);

  if (!match) {
    throw new Error(`Invalid local date: ${value}`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    throw new Error(`Invalid local date: ${value}`);
  }

  return { year, month, day };
}

function formatLocalDate(date: LocalDateParts): string {
  const year = String(date.year).padStart(4, '0');
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function addDays(date: LocalDateParts, days: number): LocalDateParts {
  return fromEpochDay(toEpochDay(date) + days);
}

function addMonths(date: LocalDateParts, months: number): LocalDateParts {
  const monthIndex = date.month - 1 + months;
  const targetYear = date.year + Math.floor(monthIndex / 12);
  const targetMonthIndex = modulo(monthIndex, 12);
  const targetMonth = targetMonthIndex + 1;
  const targetDay = Math.min(date.day, daysInMonth(targetYear, targetMonth));

  return { year: targetYear, month: targetMonth, day: targetDay };
}

function addMonthsAsInclusivePeriodEnd(
  date: LocalDateParts,
  months: number
): LocalDateParts {
  const target = addMonths(date, months);
  const wasClamped = target.day !== date.day;
  return wasClamped ? target : addDays(target, -1);
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function toEpochDay(date: LocalDateParts): number {
  return Math.floor(Date.UTC(date.year, date.month - 1, date.day) / MS_PER_DAY);
}

function fromEpochDay(epochDay: number): LocalDateParts {
  const parsed = new Date(epochDay * MS_PER_DAY);

  return {
    year: parsed.getUTCFullYear(),
    month: parsed.getUTCMonth() + 1,
    day: parsed.getUTCDate()
  };
}

function modulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

function sumEventAmounts(
  events: DashboardForecastEvent[],
  predicate: (amount: number) => boolean
): number {
  return events.reduce((total, event) => {
    if (!Number.isSafeInteger(event.amount)) {
      throw new Error(`Forecast event amount must be an integer: ${event.id}`);
    }

    return predicate(event.amount) ? total + event.amount : total;
  }, 0);
}
